import { useEffect } from "react";
import { useSettingsStore } from "../../../stores/settings";
import { Input } from "../../ui/inputs/Input";
import { TextArea } from "../../ui/inputs/TextArea";
import { addJobOpening, getJobOpening, patchJobOpening } from "../../../api/jobsOpenings";
import { showToastError, showToastSuccess } from "../../../services/toast";

const AddJobOffer = ({
  closeModal,
  update,
  id: _jobOfferId,
}: {
  closeModal: () => void;
  update: boolean;
  id: number | undefined;
}) => {
  const { formData, setFormData, updateSettings, setUpdateSettings, setJobOfferID } = useSettingsStore();

  useEffect(() => {
    if (update && _jobOfferId) {
      setJobOfferID(_jobOfferId);
      getJobOpening(_jobOfferId as number)
      .then((res) => {
        const data = res.data;
        setFormData({
          ...data,
          title: data.title,
          description: data.description,
          missions: data.missions,
          necessite: data.necessite,
          profil: data.profil,
          proposition: data.proposition,
        });
      }).catch((err) => {
        showToastError(err.response.data.message);
      });
    } else {
      setJobOfferID(undefined);
    }
  }, [update, _jobOfferId]);

  const modalTitle = update
    ? "Modifier une offre d'emploi"
    : "Ajout d'une offre d'emploi";
  const actionLabel = update ? "Remplacer" : "Ajouter";

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setJobOfferID(undefined);
        closeModal();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, [closeModal]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (update && _jobOfferId) {
      patchJobOpening(_jobOfferId as number, formData).then(() => {
        showToastSuccess("Offre d'emploi modifiée avec succès");
        setUpdateSettings(!updateSettings);
        setFormData({});
        setJobOfferID(undefined);
        closeModal();
      }).catch((err) => {
        showToastError(err.response.data.message);
      });
    } else {
      addJobOpening(formData).then(() => {
        showToastSuccess("Offre d'emploi ajoutée avec succès");
        setUpdateSettings(!updateSettings);
        setFormData({});
        setJobOfferID(undefined);
        closeModal();
      }).catch((err) => {
        showToastError(err.response.data.message);
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleChangeTextArea = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBackdropClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (event.target === event.currentTarget) {
      setJobOfferID(undefined);
      closeModal();
    }
  };

  const sectionCardClass =
    "w-full border border-light-blue/50 rounded-2xl bg-white/80 shadow-lg shadow-light-blue/30 backdrop-blur-sm";

  return (
    <>
      <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="absolute inset-0 bg-grey/60 backdrop-blur-xs transition-opacity"
          aria-hidden="true"
          onClick={handleBackdropClick}
        />
        <form
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="job-offer-modal-title"
          className="relative z-10 w-full max-w-3xl rounded-3xl border border-light-blue/40 bg-white/95 px-6 py-6 text-grey shadow-2xl shadow-light-blue/50"
        >
          <header className="flex flex-col gap-3 border-b border-light-blue/30 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2
                  id="job-offer-modal-title"
                  className="text-2xl font-semibold tracking-tight text-grey"
                >
                  {modalTitle}
                </h2>
                <span className="rounded-full bg-light-blue/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-light-blue">
                  {update ? "Edition" : "Création"}
                </span>
              </div>
              <p className="mt-1 text-sm text-grey/70">
                Complétez les informations avant de publier l'annonce.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setJobOfferID(undefined);
                closeModal();
              }}
              className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full border border-light-blue/40 text-grey transition hover:bg-light-blue/10"
            >
              <span className="sr-only">Fermer la fenêtre</span>
              <span aria-hidden="true" className="text-xl font-semibold">
                ×
              </span>
            </button>
          </header>

          <div className="custom-scroll mt-6 flex max-h-[65vh] flex-col gap-5 overflow-y-auto pr-1">
            <section className={`${sectionCardClass} p-5`}>
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-grey/70">
                Première page de l'annonce
              </h4>
              <hr className="mt-1 h-1 w-24 rounded bg-light-red border-light-red" />
              <div className="mt-6 flex flex-col gap-5">
                <Input
                  label="Titre de l'offre d'emploi"
                  name="title"
                  value={formData.title || ""}
                  onChange={handleChange}
                  clasName="w-full"
                />
                <TextArea
                  label="Description de l'offre d'emploi"
                  name="description"
                  value={formData.description || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-full"
                />
              </div>
            </section>

            <section className={`${sectionCardClass} p-5`}>
              <div className="flex flex-col gap-1">
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-grey/70">
                  Seconde page de l'annonce
                </h4>
                <hr className="h-1 w-24 rounded bg-light-red border-light-red" />
                <p className="text-xs text-grey/70 mt-2 italic">
                  Chaque retour à la ligne créera un nouvel élément de liste
                  dans l'annonce.
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-4">
                <TextArea
                  label="Description de la mission"
                  name="missions"
                  value={formData.missions || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-full"
                />
                <TextArea
                  label="Description des nécessités"
                  name="necessite"
                  value={formData.necessite || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-full"
                />
                <TextArea
                  label="Description du profil recherché"
                  name="profil"
                  value={formData.profil || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-full"
                />
                <TextArea
                  label="Description des propositions"
                  name="proposition"
                  value={formData.proposition || ""}
                  onChange={handleChangeTextArea}
                  clasName="w-full"
                />
              </div>
            </section>
          </div>

          <footer className="mt-6 flex flex-col gap-3 border-t border-light-blue/30 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => {
                setJobOfferID(undefined);
                setFormData({});
                closeModal();
              }}
              className="w-full rounded-xl border border-light-blue/40 px-4 py-2 text-sm font-semibold text-grey transition hover:bg-light-blue/10 sm:w-auto"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 sm:w-auto"
            >
              {actionLabel}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
};

export default AddJobOffer;